import React, { useState, useEffect, useContext } from 'react'
import axios from 'axios'
import { priceFormatter, percentage } from '../../../helpers/Numbers'
import RangeSlider from '../forms/RangeSlider'
import RangeSliderInput from '../forms/RangeSliderInput'
import LoanCalcTradeIn from './LoanCalcTradeIn'
import CarContext from '../../context/CarContext'
import InfoTag from '../InfoTag'

const LoanCalc = () => {

    const {car, order, setOrder, tradeIn} = useContext(CarContext)
    
    const [downPayment, setDownPayment] = useState(order.downPayment.current);
    const [duration, setDuration] = useState(order.duration.current);
    const [residual, setResidual] = useState(order.residual?.current ?? 0);
        
    
    const downPaymentMinMax = {
            min: order.downPayment.min,
            max: order.downPayment.max,
            step: order.downPayment.step,
    }

    const durationMinMax = {
            min: order.duration.min,
            max: order.duration.max,
            step: order.duration.step
    }
    const residualMinMax = {        
        min: order.residual.min,
        max: order.residual.max,
        step: order.residual.step    
    }

    useEffect(()=>{

        const calc = async()=>{

            const body = JSON.stringify({
                downPayment: downPayment ? downPayment : 0,
                residual: residual ? residual : 0,
                duration: duration ? duration : 72,
            });

            const config = {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                }
            };
        
            const url = `https://ecom.wayke.se/payment/${car._id}`;
            // console.dir({body, config, url});
            const results = await axios.post(url, body, config).catch(error =>{
                console.error(error);
            })
            
            if(results.data.response.residual == null) {
                results.data.response.residual = {min: 0, max: 0, step: 0.0, current: 0};
            }
            setOrder(results.data.response);
        }
        calc();
    },[downPayment, residual, duration])
    
    useEffect(()=>{

        if(tradeIn && tradeIn.hasOwnProperty('price') && order.downPayment.current < tradeIn.price.prediction) {
            // Minimum required downPayment, max vehiclePrice
            setDownPayment(Math.max(downPayment, Math.min(tradeIn.price.prediction, order.vehiclePrice) ))
        } else {
            setDownPayment( order.downPayment?.current ?? 0 );
        }

        setDuration( order.duration?.current ?? 72);
        setResidual( order.residual?.current ?? false);
    },[order, tradeIn])




    return (
        <>
            { order &&

                <>
                    <div className="loancalc-summary">
                        <div className="price mr-4">
                            <div>
                                <strong>Bilens&nbsp;pris</strong>
                                <div className="text-block large white ml-1">{ priceFormatter(order.vehiclePrice, ' kr') }</div>
                            </div>
                        </div>
                        <div className="mothly_cost">
                            <div className="text-block large passion">{ priceFormatter(order.monthlyCost, ' kr/mån*') }</div>
                        </div>
                    </div>
                    <div className="loancalc-silders">
                        <RangeSlider 
                            className="loancalc slider" 
                            {...downPaymentMinMax} 
                            current={downPayment} 
                            callback={setDownPayment}
                            valueFormatter={priceFormatter} 
                            car={car} 
                        >
                            <div>
                                <span>Kontantinsats</span>
                                <InfoTag className='ml-1' text="Vid ett lån med bilen som säkerhet är det lag på att långivaren måste ta ut en kontantinsats på minst 20 procent. Anledningen är att man vill minimera risken för överskuldsättning." />
                            </div>
                        </RangeSlider>
                        <RangeSlider 
                            className="loancalc slider" 
                            {...durationMinMax} 
                            current={duration} 
                            unit=" månader" 
                            callback={setDuration} 
                            car={car} 
                        >
                            <div>
                                <span>Avtalsperiod</span>
                                <InfoTag className='ml-1' text="Skulden delas upp på antal månader och längden på avtalsperioden styr hur stort beloppet blir per månad och hur långt tid det tar att betala bilen." />
                            </div>
                        </RangeSlider>
                        <RangeSlider 
                            className="loancalc slider" 
                            {...residualMinMax} 
                            disabled={order.residual.max === 0}
                            current={residual} 
                            unit="" 
                            callback={setResidual}
                            valueFormatter={percentage} 
                            car={car} 
                            >
                            <div>
                                <span>Restvärde</span>
                                <InfoTag className='ml-1' text="Billån med restvärde innebär att du inte amorterar hela bilens värde utan istället har kvar motsvarande belopp av skulden som bilen uppskattas vara värd när återbetalningstiden är slut. Vid tidsperiodens slut kvittar man bilens restvärde mot kvarvarande låneskuld." />
                            </div>
                        </RangeSlider>
                    </div>
                    <LoanCalcTradeIn />

                    <small className="conditions">* Beräknat på {(order.interest * 100).toFixed(2)}%. Kostnader som tillkommer: Uppläggningskostnad 495 kr, Administrativa avgifter 45 kr/mån</small>

                    <div className="bb-creditwarning car-card">
                        <div className="inner-container">
                            <div className="creditwarning-icon">
                                <svg width="100%" height="100%" viewBox="0 0 55 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M27.4351 29.4066V19.4609M27.4191 38.5096V37.0916M31.0332 4.04428L52.4808 41.8054C53.984 44.4659 52.0418 47.7504 48.9471 47.7504H6.05198C2.95722 47.7504 1.01502 44.4659 2.51827 41.8054L23.9658 4.04428C25.5107 1.31917 29.4883 1.31917 31.0332 4.04428Z" stroke="#D63616" stroke-width="2.4975" stroke-linecap="round"/>
                                </svg>
                            </div>
                            <div className="creditwarning-text">
                                <div className="heading">Att låna kostar pengar! </div>
                                <div>Om du inte kan betala tillbaka skulden i tid riskerar du en betalningsanmärkning. Det kan leda till svårigheter att få hyra bostad, teckna abonnemang och få nya lån. För stöd, vänd dig till budget- och skuldrådgivningen i din kommun. Kontaktuppgifter finns på <a href="https://konsumentverket.se" rel="nofollow" target="_blank">konsumentverket.se</a>. </div>
                            </div>
                        </div>
                    </div>





                </>
            }
        </>
    )

}


export default LoanCalc